import type {UpmPlantsContextValue} from '@hconnect/upm'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useTheme, useMediaQuery} from '@mui/material'
import {useEffect, useMemo, useState} from 'react'

import {MapAreaConfig, Point} from '../types'
import {calculatePlantLocations, isRegionConfigured, UpmPlantGeoData} from '../utils/geoTransform'

const areaConfigLoaders = (isSmall: boolean): Record<string, () => Promise<MapAreaConfig>> => ({
  a_NAM: () => import('../utils/loadMapConfig').then((m) => m.loadNamAreaConfig(isSmall)),
  a_EUROPE: () => import('../utils/loadMapConfig').then((m) => m.loadEuropeAreaConfig(isSmall)),
  a_AMWA: () => import('../utils/loadMapConfig').then((m) => m.loadAmwaAreaConfig()),
  a_APAC: () => import('../utils/loadMapConfig').then((m) => m.loadApacAreaConfig())
})

const intersectPlantsWithEquipment = (
  upmPlants: UpmPlantsContextValue | undefined,
  plantIds: string[]
): UpmPlantGeoData[] => {
  if (!upmPlants) return []

  const plantIdSet = new Set(plantIds)
  return Object.entries(upmPlants)
    .filter(([plantId]) => plantIdSet.has(plantId))
    .map(([plantId, plant]) => ({
      plantId,
      latitude: plant?.latitude ?? null,
      longitude: plant?.longitude ?? null
    }))
}

const mergeWithDynamicLocations = (
  staticConfig: MapAreaConfig,
  dynamicLocations: Record<string, Point>
): MapAreaConfig => ({
  ...staticConfig,
  plantLocationMap: {
    ...staticConfig.plantLocationMap,
    ...dynamicLocations
  }
})

export const useMapAreaConfig = (areaId: string | undefined, plantIds: string[] = []) => {
  const [config, setConfig] = useState<MapAreaConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const upmPlants = useUpmPlantsContext()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (!areaId) {
      setIsLoading(false)
      return
    }

    const loader = areaConfigLoaders(isSmall)[areaId]
    if (!loader) {
      setIsLoading(false)
      return
    }

    let isCancelled = false
    setIsLoading(true)

    loader()
      .then((cfg) => {
        if (!isCancelled) {
          setConfig(cfg)
          setIsLoading(false)
        }
      })
      .catch((error) => {
        console.error(`Failed to load map config for area ${areaId}:`, error)
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [areaId, isSmall])

  const currentConfig = useMemo(() => {
    if (!config || !areaId) return config
    if (!isRegionConfigured(areaId)) return config
    if (plantIds.length === 0) return config

    const plants = intersectPlantsWithEquipment(upmPlants, plantIds)
    const dynamicLocations = calculatePlantLocations(areaId, plants)

    if (Object.keys(dynamicLocations).length === 0) return config

    return mergeWithDynamicLocations(config, dynamicLocations)
  }, [config, areaId, upmPlants, plantIds])

  return {mapAreaConfig: currentConfig, isMapConfigLoading: isLoading}
}
